import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { clientsAPI, usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import LangSwitch from '../components/LangSwitch';
import { toast } from 'sonner';
import {
  Users, Wallet, TrendingUp, Plus, LogOut, MoreVertical, Eye, Pencil, Trash2, UserPlus, Shield, Settings, KeyRound
} from 'lucide-react';

export default function AdminDashboard() {
  const { logout, user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newClient, setNewClient] = useState({ client_name: '', deposit: 0, date_joined: '' });
  const [newUser, setNewUser] = useState({ username: '', password: '', role: 'client', client_id: '' });
  const [showChangePassword, setShowChangePassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadData = useCallback(async () => {
    try {
      const [clientsRes, usersRes] = await Promise.all([clientsAPI.list(), usersAPI.list()]);
      setClients(clientsRes.data);
      setUsers(usersRes.data);
    } catch {
      toast.error(t('admin_load_error'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { loadData(); }, [loadData]);

  const totalDeposits = clients.reduce((s, c) => s + (c.deposit || 0), 0);
  const totalBalance = clients.reduce((s, c) => s + (c.balance || 0), 0);
  const totalReturn = clients.reduce((s, c) => s + (c.return_amount || 0), 0);

  const handleCreateClient = async () => {
    try {
      await clientsAPI.create(newClient);
      toast.success(t('admin_client_created'));
      setShowNewClient(false);
      setNewClient({ client_name: '', deposit: 0, date_joined: '' });
      loadData();
    } catch { toast.error(t('common_error')); }
  };

  const handleCreateUser = async () => {
    try {
      await usersAPI.create(newUser);
      toast.success(t('admin_user_created'));
      setShowNewUser(false);
      setNewUser({ username: '', password: '', role: 'client', client_id: '' });
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || t('common_error')); }
  };

  const handleDeleteClient = async (id) => {
    if (!window.confirm(t('admin_confirm_delete'))) return;
    try {
      await clientsAPI.delete(id);
      toast.success(t('admin_client_deleted'));
      loadData();
    } catch { toast.error(t('common_error')); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0C15' }}>
      <div className="text-[#94A3B8]">{t('common_loading')}</div>
    </div>
  );

  return (
    <div className="min-h-screen" style={{ background: '#0B0C15' }} data-testid="admin-dashboard">
      <header className="border-b border-[#2D2E3D] bg-[#151621]/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-lg font-bold tracking-tight text-white">ALGO PREMIUM</span>
            <Badge className="bg-[#7C3AED]/20 text-[#7C3AED] border-[#7C3AED]/30 text-xs ml-2">{t('admin_badge')}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <LangSwitch />
            <span className="text-sm text-[#94A3B8]">{user?.username}</span>
            <Button data-testid="admin-logout-button" variant="ghost" size="icon" className="text-[#64748B] hover:text-white hover:bg-[#1E1F2E]" onClick={logout}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: t('admin_clients'), value: clients.length, icon: Users, color: '#7C3AED' },
            { label: t('admin_total_deposits'), value: fmt(totalDeposits), icon: Wallet, color: '#0EA5E9' },
            { label: t('admin_total_balance'), value: fmt(totalBalance), icon: TrendingUp, color: '#10B981' },
            { label: t('admin_total_return'), value: fmt(totalReturn), icon: TrendingUp, color: '#F59E0B' },
          ].map((stat, i) => (
            <Card key={i} className={`bg-[#151621] border-[#2D2E3D] opacity-0 animate-fade-in-up stagger-${i + 1}`}>
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-[#94A3B8]">{stat.label}</span>
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${stat.color}20` }}>
                    <stat.icon className="w-4 h-4" style={{ color: stat.color }} />
                  </div>
                </div>
                <div className="text-xl font-bold text-white font-mono">{stat.value}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Clients Table */}
        <Card className="bg-[#151621] border-[#2D2E3D] mb-8">
          <CardHeader className="border-b border-[#2D2E3D]/50 flex-row items-center justify-between">
            <CardTitle className="text-lg text-white">{t('admin_clients')}</CardTitle>
            <div className="flex gap-2">
              <Button data-testid="add-user-button" size="sm" className="bg-[#1E1F2E] hover:bg-[#2D2E3D] text-white border border-[#2D2E3D] text-xs" onClick={() => setShowNewUser(true)}>
                <UserPlus className="w-3.5 h-3.5 mr-1" /> {t('admin_user')}
              </Button>
              <Button data-testid="add-client-button" size="sm" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white text-xs shadow-[0_0_10px_rgba(124,58,237,0.2)]" onClick={() => setShowNewClient(true)}>
                <Plus className="w-3.5 h-3.5 mr-1" /> {t('admin_new_client')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1E1F2E] hover:bg-[#1E1F2E] border-[#2D2E3D]/50">
                  <TableHead className="text-[#94A3B8] font-medium px-6 py-3">Client</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_deposit')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_balance')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_return')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_cumul_pct')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_monthly_pct')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_member_since')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium text-right pr-6">{t('admin_actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clients.map((c) => (
                  <TableRow key={c.id} className="border-[#2D2E3D]/50 hover:bg-[#1E1F2E]/50 transition-colors">
                    <TableCell className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-[#7C3AED]/20 flex items-center justify-center text-[#7C3AED] font-semibold text-xs">
                          {c.client_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                        </div>
                        <span className="text-white font-medium text-sm">{c.client_name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-[#94A3B8]">{fmt(c.deposit)}</TableCell>
                    <TableCell className="font-mono text-sm text-white">{fmt(c.balance)}</TableCell>
                    <TableCell className="font-mono text-sm text-[#10B981]">{fmt(c.return_amount)}</TableCell>
                    <TableCell>
                      <Badge className="bg-[#10B981]/20 text-[#10B981] border-0 font-mono text-xs">{c.return_pct?.toFixed(2)}%</Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className="bg-[#0EA5E9]/20 text-[#0EA5E9] border-0 font-mono text-xs">{(c.monthly_return_pct || 0).toFixed(2)}%</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-[#94A3B8]">{c.date_joined}</TableCell>
                    <TableCell className="text-right pr-6">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button data-testid={`client-actions-${c.id}`} variant="ghost" size="icon" className="h-8 w-8 text-[#64748B] hover:text-white">
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="bg-[#151621] border-[#2D2E3D] text-white">
                          <DropdownMenuItem data-testid={`view-report-${c.id}`} className="hover:bg-[#1E1F2E] cursor-pointer" onClick={() => navigate(`/report/${c.id}`)}>
                            <Eye className="w-4 h-4 mr-2" /> {t('admin_view_report')}
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`edit-client-${c.id}`} className="hover:bg-[#1E1F2E] cursor-pointer" onClick={() => navigate(`/admin/client/${c.id}`)}>
                            <Pencil className="w-4 h-4 mr-2" /> {t('admin_edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem data-testid={`delete-client-${c.id}`} className="hover:bg-[#1E1F2E] text-red-400 cursor-pointer" onClick={() => handleDeleteClient(c.id)}>
                            <Trash2 className="w-4 h-4 mr-2" /> {t('admin_delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {clients.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-12 text-[#64748B]">{t('admin_no_clients')}</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card className="bg-[#151621] border-[#2D2E3D]">
          <CardHeader className="border-b border-[#2D2E3D]/50">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#94A3B8]" /> {t('admin_users')}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#1E1F2E] hover:bg-[#1E1F2E] border-[#2D2E3D]/50">
                  <TableHead className="text-[#94A3B8] font-medium px-6 py-3">{t('admin_username')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_role')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium">{t('admin_linked_client')}</TableHead>
                  <TableHead className="text-[#94A3B8] font-medium text-right pr-6">{t('admin_actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-[#2D2E3D]/50 hover:bg-[#1E1F2E]/50">
                    <TableCell className="px-6 py-3 text-white text-sm">{u.username}</TableCell>
                    <TableCell>
                      <Badge className={u.role === 'admin' ? 'bg-[#7C3AED]/20 text-[#7C3AED] border-0 text-xs' : 'bg-[#0EA5E9]/20 text-[#0EA5E9] border-0 text-xs'}>{u.role}</Badge>
                    </TableCell>
                    <TableCell className="text-sm text-[#94A3B8]">
                      {u.client_id ? clients.find(c => c.id === u.client_id)?.client_name || u.client_id : '—'}
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          data-testid={`change-password-${u.id}`}
                          variant="ghost"
                          size="sm"
                          className="text-[#64748B] hover:text-white text-xs h-7"
                          onClick={() => { setShowChangePassword(u); setNewPassword(''); }}
                        >
                          <KeyRound className="w-3.5 h-3.5 mr-1" /> {t('admin_change_password')}
                        </Button>
                        {u.role !== 'admin' && (
                          <Button
                            data-testid={`delete-user-${u.id}`}
                            variant="ghost"
                            size="sm"
                            className="text-[#64748B] hover:text-red-400 text-xs h-7"
                            onClick={async () => {
                              if (!window.confirm(`Supprimer l'utilisateur ${u.username} ?`)) return;
                              try {
                                await usersAPI.delete(u.id);
                                toast.success('Utilisateur supprime');
                                loadData();
                              } catch { toast.error(t('common_error')); }
                            }}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* New Client Dialog */}
      <Dialog open={showNewClient} onOpenChange={setShowNewClient}>
        <DialogContent className="bg-[#151621] border-[#2D2E3D] text-white max-w-md">
          <DialogHeader><DialogTitle>{t('admin_new_client')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_client_name')}</label>
              <Input data-testid="new-client-name" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={newClient.client_name} onChange={(e) => setNewClient({ ...newClient, client_name: e.target.value })} />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_initial_deposit')}</label>
              <Input data-testid="new-client-deposit" type="number" className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono" value={newClient.deposit} onChange={(e) => setNewClient({ ...newClient, deposit: parseFloat(e.target.value) || 0 })} />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_join_date')}</label>
              <Input data-testid="new-client-date" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" placeholder="01/01/2026" value={newClient.date_joined} onChange={(e) => setNewClient({ ...newClient, date_joined: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-[#94A3B8]" onClick={() => setShowNewClient(false)}>{t('admin_cancel')}</Button>
            <Button data-testid="confirm-create-client" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleCreateClient}>{t('admin_create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* New User Dialog */}
      <Dialog open={showNewUser} onOpenChange={setShowNewUser}>
        <DialogContent className="bg-[#151621] border-[#2D2E3D] text-white max-w-md">
          <DialogHeader><DialogTitle>{t('admin_new_user')}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_username')}</label>
              <Input data-testid="new-user-username" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_password')}</label>
              <Input data-testid="new-user-password" type="password" className="bg-[#1E1F2E] border-[#2D2E3D] text-white" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_role')}</label>
              <select data-testid="new-user-role" className="w-full h-9 rounded-md border border-[#2D2E3D] bg-[#1E1F2E] px-3 text-sm text-white" value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}>
                <option value="client">Client</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            {newUser.role === 'client' && (
              <div>
                <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_linked_client')}</label>
                <select data-testid="new-user-client" className="w-full h-9 rounded-md border border-[#2D2E3D] bg-[#1E1F2E] px-3 text-sm text-white" value={newUser.client_id} onChange={(e) => setNewUser({ ...newUser, client_id: e.target.value })}>
                  <option value="">{t('admin_select')}</option>
                  {clients.map(c => (<option key={c.id} value={c.id}>{c.client_name}</option>))}
                </select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-[#94A3B8]" onClick={() => setShowNewUser(false)}>{t('admin_cancel')}</Button>
            <Button data-testid="confirm-create-user" className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white" onClick={handleCreateUser}>{t('admin_create')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Password Dialog */}
      <Dialog open={!!showChangePassword} onOpenChange={() => setShowChangePassword(null)}>
        <DialogContent className="bg-[#151621] border-[#2D2E3D] text-white max-w-md">
          <DialogHeader><DialogTitle>{t('admin_change_password')} — {showChangePassword?.username}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">{t('admin_new_password')}</label>
              <Input
                data-testid="new-password-input"
                type="text"
                className="bg-[#1E1F2E] border-[#2D2E3D] text-white font-mono"
                placeholder={t('admin_new_password')}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" className="text-[#94A3B8]" onClick={() => setShowChangePassword(null)}>{t('admin_cancel')}</Button>
            <Button
              data-testid="confirm-change-password"
              className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white"
              onClick={async () => {
                if (!newPassword) return;
                try {
                  await usersAPI.changePassword(showChangePassword.id, newPassword);
                  toast.success(t('admin_password_changed'));
                  setShowChangePassword(null);
                  setNewPassword('');
                } catch { toast.error(t('common_error')); }
              }}
            >
              {t('edit_save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
